// What a habit expects on a day, and the statistics derived from its completed
// days: streaks, completion rate, calendar states. Pure functions, shared by the
// PWA (all statistics are computed in the browser) and the tests.
import type { Habit, Schedule } from './api';
import { addDays, diffDays, startOfWeek, weekday, type DayKey } from './days';

export type DoneSet = ReadonlySet<DayKey>;

/** The habit expects a completion on this weekday (weekly habits: every day is possible). */
export function isScheduledDay(schedule: Schedule, day: DayKey): boolean {
  return schedule.kind === 'weekdays' ? schedule.days.includes(weekday(day)) : true;
}

/** Completions in the Monday–Sunday week of `day`. */
export function weekCount(done: DoneSet, day: DayKey): number {
  const monday = startOfWeek(day);
  let count = 0;
  for (let i = 0; i < 7; i++) if (done.has(addDays(monday, i))) count++;
  return count;
}

/** First day that counts: the start day, or an earlier back-filled completion. */
export function firstDay(habit: Pick<Habit, 'startDay'>, done: DoneSet): DayKey {
  let first = habit.startDay;
  for (const day of done) if (day < first) first = day;
  return first;
}

/**
 * The habit belongs on the list of `day`: scheduled that day, or for weekly
 * habits while the week's target is still open (or it was done that day).
 */
export function isDue(habit: Habit, done: DoneSet, day: DayKey): boolean {
  if (day < firstDay(habit, done)) return false;
  if (done.has(day)) return true;
  if (habit.schedule.kind === 'weekly') return weekCount(done, day) < habit.schedule.times;
  return isScheduledDay(habit.schedule, day);
}

export type StreakUnit = 'days' | 'weeks';

export interface Streaks {
  current: number;
  best: number;
  unit: StreakUnit;
}

/**
 * daily/weekdays: completed scheduled days in a row (a completion on an off day
 * counts too, an off day without one does not break the streak). weekly: weeks
 * in a row that reached the target. Today, and the current week, never break a
 * streak while still open.
 */
export function streaks(habit: Habit, done: DoneSet, today: DayKey): Streaks {
  const start = firstDay(habit, done);
  if (habit.schedule.kind === 'weekly') {
    const target = habit.schedule.times;
    const thisWeek = startOfWeek(today);
    let best = 0;
    let run = 0;
    let current = 0;
    for (let week = startOfWeek(start); week <= thisWeek; week = addDays(week, 7)) {
      const met = weekCount(done, week) >= target;
      if (met) run++;
      else if (week !== thisWeek) run = 0;
      best = Math.max(best, run);
      if (week === thisWeek) current = run;
    }
    return { current, best, unit: 'weeks' };
  }

  let best = 0;
  let run = 0;
  for (let day = start; day <= today; day = addDays(day, 1)) {
    if (done.has(day)) run++;
    else if (isScheduledDay(habit.schedule, day) && day !== today) run = 0;
    best = Math.max(best, run);
  }
  return { current: run, best, unit: 'days' };
}

export interface Rate {
  achieved: number;
  expected: number;
  /** 0…1, null without any expectation yet. */
  ratio: number | null;
}

/**
 * Share of the expected completions within the last `windowDays` days
 * (weekly habits: share of weeks that reached the target). An open today or
 * current week counts only once it is reached.
 */
export function completionRate(habit: Habit, done: DoneSet, today: DayKey, windowDays = 30): Rate {
  const start = firstDay(habit, done);
  const from = [addDays(today, -(windowDays - 1)), start].sort().at(-1)!;
  let achieved = 0;
  let expected = 0;
  if (habit.schedule.kind === 'weekly') {
    const thisWeek = startOfWeek(today);
    for (let week = startOfWeek(from); week <= thisWeek; week = addDays(week, 7)) {
      const met = weekCount(done, week) >= habit.schedule.times;
      if (week === thisWeek && !met) continue;
      expected++;
      if (met) achieved++;
    }
  } else {
    for (let day = from; day <= today; day = addDays(day, 1)) {
      if (!isScheduledDay(habit.schedule, day)) continue;
      const isDone = done.has(day);
      if (day === today && !isDone) continue;
      expected++;
      if (isDone) achieved++;
    }
  }
  return { achieved, expected, ratio: expected > 0 ? achieved / expected : null };
}

/**
 * Calendar state of a day. done: completed. missed: scheduled, past, not done.
 * open: today (or this week for weekly habits) and still possible. off: not
 * scheduled (weekly habits: a past day without completion). future / before: outside the habit's time.
 */
export type DayState = 'done' | 'missed' | 'open' | 'off' | 'future' | 'before';

export function dayState(habit: Habit, done: DoneSet, day: DayKey, today: DayKey, start = firstDay(habit, done)): DayState {
  if (done.has(day)) return 'done';
  if (day > today) return 'future';
  if (day < start) return 'before';
  if (habit.schedule.kind === 'weekly') return day === today ? 'open' : 'off';
  if (!isScheduledDay(habit.schedule, day)) return 'off';
  return day === today ? 'open' : 'missed';
}

/** Human-readable schedule, e.g. "Täglich", "Mo, Mi, Fr", "3× pro Woche". */
export function scheduleLabel(schedule: Schedule): string {
  switch (schedule.kind) {
    case 'daily':
      return schedule.times && schedule.times > 1 ? `${schedule.times}× pro Tag` : 'Täglich';
    case 'weekly':
      return schedule.times === 1 ? '1× pro Woche' : `${schedule.times}× pro Woche`;
    case 'weekdays': {
      const days = schedule.days;
      if (days.length === 5 && days.every((d) => d < 5)) return 'Werktags';
      if (days.length === 2 && days[0] === 5 && days[1] === 6) return 'Am Wochenende';
      return days.map((d) => WEEKDAY_SHORT[d]).join(', ');
    }
  }
}

export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

/** Days from the start until today (inclusive), at least 1. */
export function daysActive(habit: Habit, done: DoneSet, today: DayKey): number {
  return Math.max(1, diffDays(firstDay(habit, done), today) + 1);
}
